import { Controller } from "@hotwired/stimulus"

// Wraps a lazy-loaded turbo-frame with timeout detection and retry.
// Shows a spinner while loading, and a retry link if the request
// times out or errors.
//
// Usage:
//   <turbo-frame id="place_count" src="..." loading="lazy"
//     data-controller="frame-retry" data-frame-retry-timeout-value="30000">
//     <template data-frame-retry-target="spinner">
//       <svg class="animate-spin ...">...</svg>
//     </template>
//     <template data-frame-retry-target="retryTemplate">
//       <button data-action="frame-retry#retry" class="...">retry</button>
//     </template>
//     <span data-frame-retry-target="placeholder" class="text-gray-300">...</span>
//   </turbo-frame>
//
export default class extends Controller {
  static targets = ["spinner", "retryTemplate", "placeholder"]
  static values = { timeout: { type: Number, default: 30000 } }

  connect() {
    this.loaded = false
    this.element.addEventListener("turbo:frame-load", this.handleLoad)
    this.startTimer()
    this.showSpinner()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.handleLoad)
    clearTimeout(this.timer)
  }

  handleLoad = () => {
    this.loaded = true
    clearTimeout(this.timer)
  }

  retry() {
    const src = this.element.getAttribute("src")
    if (!src) return

    this.loaded = false
    this.showSpinner()
    this.element.removeAttribute("src")
    this.element.setAttribute("src", src)
    this.startTimer()
  }

  // private

  startTimer() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      if (!this.loaded) this.showRetry()
    }, this.timeoutValue)
  }

  showSpinner() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
    if (this.hasRetryNode) this.retryNode.remove()
    if (this.hasSpinnerTarget) {
      this.spinnerClone = this.spinnerTarget.content.cloneNode(true).firstElementChild
      this.element.appendChild(this.spinnerClone)
    }
  }

  showRetry() {
    if (this.spinnerClone) this.spinnerClone.remove()
    if (this.hasRetryTemplateTarget) {
      this.retryNode = this.retryTemplateTarget.content.cloneNode(true).firstElementChild
      this.element.appendChild(this.retryNode)
    }
  }
}
